<template>
  <b-modal
    v-model="innerOpen"
    size="xl"
    modal-class="mc-rec-order-modal-wrap"
    dialog-class="mc-rec-order-modal-dialog"
    content-class="mc-rec-order-modal-content p-0 border-0"
    body-class="p-0"
    hide-header
    hide-footer
    centered
    @hidden="onHidden"
  >
    <div v-if="orderRaw" class="mc-rec-ord-root">
      <RecommendationOrderHeader
        :order-id="String(normalized.orderId)"
        :subtitle-line="subtitleLine"
        @close="fechar"
      />
      <RecommendationOrderSummary
        :recommended-count="normalized.recommendedCount"
        :purchased-count="normalized.purchasedCount"
        :converted-count="normalized.convertedCount"
        :conversion-rate-label="normalized.conversionRateLabel"
      />
      <RecommendationOrderFilters v-model="filterTab" />
      <div class="mc-rec-ord-body">
        <template v-if="filterTab === 'all'">
          <RecommendationEmptyState
            v-if="normalized.showNoRecommendationMessage"
            message="Nenhuma recomendação foi enviada para este lead antes deste pedido."
          />
          <RecommendationEmptyState
            v-if="normalized.showNoPurchasedMessage"
            :muted="true"
            message="Nenhum produto comprado foi encontrado para este pedido."
          />
          <RecommendationEmptyState
            v-if="normalized.showNoConversionMessage"
            message="Nenhum produto recomendado foi comprado neste pedido."
          />
        </template>

        <p v-if="filterEmptyHint" class="mc-rec-ord-filter-empty">{{ filterEmptyHint }}</p>

        <RecommendationGroupSection
          v-if="showConvertedSection"
          title="Convertidos"
          variant="converted"
          icon="✓"
          :items="normalized.convertedProducts"
        >
          <template slot-scope="slotProps">
            <RecommendationProductCard
              v-for="(item, idx) in slotProps.displayItems"
              :key="'cv-' + idx"
              :item="item"
              variant="converted"
            >
              <template slot="badges">
                <span class="mc-rec-ord-badge mc-rec-ord-badge--ok">Recomendado e comprado</span>
                <span
                  v-if="item.algorithm"
                  class="mc-rec-ord-badge mc-rec-ord-badge--rec"
                >{{ item.algorithm }}</span>
                <span
                  v-if="item.quantidade != null && item.quantidade !== ''"
                  class="mc-rec-ord-badge mc-rec-ord-badge--muted"
                >Qtd. {{ item.quantidade }}</span>
              </template>
            </RecommendationProductCard>
          </template>
        </RecommendationGroupSection>

        <RecommendationGroupSection
          v-if="showRecommendedSection"
          title="Recomendados não comprados"
          variant="recommended"
          :items="normalized.recommendedNotPurchased"
        >
          <template slot-scope="slotProps">
            <RecommendationProductCard
              v-for="(item, idx) in slotProps.displayItems"
              :key="'nr-' + idx"
              :item="item"
              variant="recommended"
            >
              <template slot="badges">
                <span class="mc-rec-ord-badge mc-rec-ord-badge--rec">Recomendado</span>
                <span class="mc-rec-ord-badge mc-rec-ord-badge--muted">Não comprado</span>
              </template>
            </RecommendationProductCard>
          </template>
        </RecommendationGroupSection>

        <RecommendationGroupSection
          v-if="showSpontaneousSection"
          title="Outros produtos comprados"
          variant="spontaneous"
          :items="normalized.purchasedNotRecommended"
        >
          <template slot-scope="slotProps">
            <RecommendationProductCard
              v-for="(item, idx) in slotProps.displayItems"
              :key="'sp-' + idx"
              :item="item"
              variant="spontaneous"
            >
              <template slot="badges">
                <span class="mc-rec-ord-badge mc-rec-ord-badge--coral">Compra espontânea</span>
                <span
                  v-if="item.quantidade != null && item.quantidade !== ''"
                  class="mc-rec-ord-badge mc-rec-ord-badge--muted"
                >Qtd. {{ item.quantidade }}</span>
              </template>
            </RecommendationProductCard>
          </template>
        </RecommendationGroupSection>
      </div>
      <footer class="mc-rec-ord-modal-footer">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mc-rec-ord-modal-footer__btn"
          @click="fechar"
        >
          Fechar
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton } from 'bootstrap-vue'
import dayjs from 'dayjs'
import normalizeRecommendationOrderData from './normalizeRecommendationOrderData'
import RecommendationOrderHeader from './RecommendationOrderHeader.vue'
import RecommendationOrderSummary from './RecommendationOrderSummary.vue'
import RecommendationOrderFilters from './RecommendationOrderFilters.vue'
import RecommendationGroupSection from './RecommendationGroupSection.vue'
import RecommendationProductCard from './RecommendationProductCard.vue'
import RecommendationEmptyState from './RecommendationEmptyState.vue'

export default {
  name: 'RecommendationOrderModal',
  components: {
    BModal,
    BButton,
    RecommendationOrderHeader,
    RecommendationOrderSummary,
    RecommendationOrderFilters,
    RecommendationGroupSection,
    RecommendationProductCard,
    RecommendationEmptyState,
  },
  props: {
    value: { type: Boolean, default: false },
    /** Pedido cru (legado ou novo formato API) */
    orderRaw: { type: Object, default: null },
  },
  data() {
    return {
      innerOpen: false,
      filterTab: 'all',
    }
  },
  computed: {
    normalized() {
      return normalizeRecommendationOrderData(this.orderRaw || {})
    },
    subtitleLine() {
      const n = this.normalized
      const lead = n.leadName || 'Lead'
      const base = `${lead} via WhatsApp`
      if (!n.purchasedAt) return base
      return `${base} · Comprado em ${dayjs(n.purchasedAt).format('DD/MM/YYYY HH:mm')}`
    },
    showConvertedSection() {
      const f = this.filterTab
      const n = this.normalized
      if (f === 'recommended' || f === 'purchased') return false
      if (f === 'converted') return n.convertedProducts.length > 0
      return n.convertedProducts.length > 0
    },
    showRecommendedSection() {
      const f = this.filterTab
      const n = this.normalized
      if (f === 'converted' || f === 'purchased') return false
      if (f === 'recommended') {
        return n.hasRecommendations && n.recommendedNotPurchased.length > 0
      }
      return n.hasRecommendations && n.recommendedNotPurchased.length > 0
    },
    showSpontaneousSection() {
      const f = this.filterTab
      const n = this.normalized
      if (f === 'converted' || f === 'recommended') return false
      if (f === 'purchased') return n.purchasedNotRecommended.length > 0
      return n.purchasedNotRecommended.length > 0
    },
    filterEmptyHint() {
      const f = this.filterTab
      if (f === 'all') return ''
      const n = this.normalized
      if (f === 'converted' && !n.convertedProducts.length) {
        return 'Nenhum produto convertido neste pedido.'
      }
      if (f === 'recommended') {
        if (!n.hasRecommendations) {
          return 'Nenhuma recomendação foi enviada para este lead antes deste pedido.'
        }
        if (!n.recommendedNotPurchased.length) {
          return 'Não há recomendados não comprados para exibir.'
        }
      }
      if (f === 'purchased' && !n.purchasedNotRecommended.length) {
        return 'Não há compras fora da recomendação para exibir.'
      }
      return ''
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.innerOpen = val
        if (val) this.filterTab = 'all'
      },
    },
    innerOpen(val) {
      this.$emit('input', val)
    },
  },
  methods: {
    fechar() {
      this.innerOpen = false
    },
    onHidden() {
      this.$emit('input', false)
      this.$emit('hidden')
    },
  },
}
</script>
